import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, createBlock as _createBlock, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = { class: "mb-l pb-xs border-gray-200 border-b-2 flex items-center justify-between" }
const _hoisted_2 = { class: "flex items-center justify-between" }
const _hoisted_3 = { class: "mt-4 divide-y" }
const _hoisted_4 = {
  key: 0,
  class: "text-sm mb-4"
}
const _hoisted_5 = /*#__PURE__*/_createElementVNode("br", null, null, -1)
const _hoisted_6 = { class: "text-xs text-gray-600" }
const _hoisted_7 = { class: "flex" }

import { computed, ref, watch } from "vue";
import { useManagedPatientsStore } from "@/store/managedPatientsStore";
import { ManagedPatient, ManagedPatientKey } from "@/interfaces/managedPatient";
import { supabase } from "@/helpers/supabase";
import { useSessionStore } from "@/store/sessionStore";
import { encryptSymmetric } from "@/helpers/encryption";
import DialogCard from "@/components/DialogCard.vue";
import PatientFile from "./PatientFile.vue";
import UmanaHeadline from "@/components/UmanaHeadline.vue";
import IconButton from "@/components/IconButton.vue";
import ListTile from "@/components/ListTile.vue";
import CoreIcon from "@/components/CoreIcon.vue";
import MedicationHistory from "./MedicationHistory.vue";
import { usePublicStore } from "@/store/publicStore";
import MedicationInformation from "@/components/MedicationInformation.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'PatientMedications',
  props: {
    managedPatient: {},
    managedPatientKey: {}
  },
  setup(__props: any) {

const props = __props;

const managedPatientsStore = useManagedPatientsStore();
const isLoadingMedicationInformation = ref(true);
const errorLoadingMedicationInformation = ref(false);

managedPatientsStore
  .fetchMedicationsInformation(props.managedPatient.id)
  .catch(() => {
    errorLoadingMedicationInformation.value = true;
  })
  .finally(() => {
    isLoadingMedicationInformation.value = false;
  });
const sessionStore = useSessionStore();
const publicStore = usePublicStore();
const managedPatient = computed(() => props.managedPatient);

const isAddingMedication = ref<boolean>(false);
const editingMedication = ref<number | null>(null);
const history = ref<number | null>(null);

const connectedShopCareProviderGroups = computed(() => {
  const connectedCPGIds = managedPatientsStore.connectedCareProviderGroups?.get(
    managedPatient.value.id
  );
  const connectedShopCareProviderGroups =
    publicStore.careProviderGroups?.filter((cpg) => {
      if (connectedCPGIds) {
        return connectedCPGIds.has(cpg.id) && cpg.is_shop;
      }
    });
  connectedShopCareProviderGroups?.sort((a, b) => {
    return a.group_name.localeCompare(b.group_name);
  });
  return connectedShopCareProviderGroups;
});

// medication
const currentMedication = computed(() => {
  const patientMedications = (
    managedPatient.value?.currentMedication ?? []
  ).sort((med: any) => med.gtin);

  return patientMedications;
});

const files = computed(() => {
  return managedPatient.value?.history.filter((h) => h.type == 3);
});

const fileSelectOptions = computed(() => {
  let fileSelectOptions = [];
  for (let file of files.value) {
    fileSelectOptions.push({
      name: file.decrypted_data.fileName,
      path: file.file_path,
    });
  }
  return fileSelectOptions;
});

const medicationModel = ref<any>({});
function addMedication() {
  isAddingMedication.value = true;
  editingMedication.value = null;
  medicationModel.value = {};
}

async function editMedication(medication: any) {
  selectedSupplier.value = medication.supplierId;
  isAddingMedication.value = true;
  editingMedication.value = medication.createdAt;
  medicationModel.value = {
    medication: {
      dscrd: medication.dscrd,
      gtin: medication.gtin,
      nincd: medication.nincd,
      care_provider_group_id: medication.supplierId,
      supplierId: medication.care_provider_group_id,
      public_price: medication.public_price,
      storage_quantity: medication.storage_quantity,
      pharmacode: medication.pharmacode,
      isInIndex: medication.isInIndex,
      cool_storage: medication.cool_storage,
    },
    file: medication.file
      ? { path: medication.file, name: documentNameWithPath(medication.file) }
      : undefined,
    createdAt: medication.createdAt,
  };
}

function documentWithPath(path: string) {
  return files.value.find((file) => file.file_path == path);
}
function documentNameWithPath(path: string) {
  return files.value.find((file) => file.file_path == path)?.decrypted_data
    .fileName;
}

async function saveMedication() {
  let newMedication = JSON.parse(JSON.stringify(currentMedication.value));
  if (editingMedication.value) {
    newMedication = newMedication.filter(
      (medication: any) => medication.createdAt != editingMedication.value
    );
  }

  newMedication.push({
    dscrd: medicationModel.value.medication.dscrd,
    gtin: medicationModel.value.medication.gtin ?? 0,
    file: medicationModel.value.file?.path,
    createdAt:
      editingMedication.value ??
      `${new Date().getTime()}-${Math.round(Math.random() * 10000)}`,
    supplierId:
      medicationModel.value.medication.care_provider_group_id ??
      selectedSupplier.value,
    public_price: medicationModel.value.medication.public_price,
    storage_quantity: medicationModel.value.medication.storage_quantity,
    pharmacode: medicationModel.value.medication.pharmacode ?? -1,
    isInIndex: true,
    cool_storage: medicationModel.value.medication.cool_storage,
  });
  editingMedication.value = null;
  await encryptAndSaveMedication(newMedication);

  window._paq.push(["trackEvent", "PatientMedication", "update", undefined]);
}

async function removeMedication(removeCreatedAt: any) {
  let oldMedication = JSON.parse(JSON.stringify(currentMedication.value));
  let newMedication = oldMedication.filter(
    (medication: any) => medication.createdAt != removeCreatedAt
  );
  await encryptAndSaveMedication(newMedication);
  window._paq.push(["trackEvent", "PatientMedication", "delete", undefined]);
}

async function encryptAndSaveMedication(newMedication: Array<any>) {
  if (
    !props.managedPatientKey.decrypted_symmetric_key ||
    !managedPatient.value?.id ||
    !sessionStore.selectedCareProviderGroup?.id
  )
    return;

  const encryptedMedication = await encryptSymmetric(
    JSON.stringify(newMedication),
    props.managedPatientKey.decrypted_symmetric_key
  );

  await supabase
    .from("managedPatient_data")
    .insert({
      managed_patient_id: managedPatient.value.id,
      used_key_id: props.managedPatientKey.key_id,
      nonce: encryptedMedication.nonce,
      encrypted_data: encryptedMedication.encryptedMessage,
      owner_care_provider_group_id: sessionStore.selectedCareProviderGroup.id,
      type: 2,
    })
    .returns<ManagedPatient[]>();

  await managedPatientsStore.updatePatients();
  isAddingMedication.value = false;
}

function changelog() {
  history.value = props.managedPatient.id;
}

const selectedSupplier = ref(
  connectedShopCareProviderGroups.value?.length != 0
    ? connectedShopCareProviderGroups?.value?.[0].id
    : -1
);

watch(connectedShopCareProviderGroups, () => {
  selectedSupplier.value =
    connectedShopCareProviderGroups.value?.length != 0
      ? connectedShopCareProviderGroups?.value?.[0].id
      : -1;
});

return (_ctx: any,_cache: any) => {
  const _component_FormKit = _resolveComponent("FormKit")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("div", _hoisted_1, [
      _createVNode(UmanaHeadline, {
        h: "h2",
        class: "line-clamp-1"
      }, {
        default: _withCtx(() => [
          _createTextVNode(" Medikamente ")
        ]),
        _: 1
      }),
      _createElementVNode("div", _hoisted_2, [
        _createVNode(IconButton, {
          onClick: _cache[0] || (_cache[0] = ($event: any) => (changelog())),
          icon: "changeLog"
        }),
        _createVNode(IconButton, {
          onClick: _cache[1] || (_cache[1] = ($event: any) => (addMedication())),
          icon: "plus",
          class: "ml-xs"
        })
      ])
    ]),
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(connectedShopCareProviderGroups.value, (cpg) => {
      return (_openBlock(), _createElementBlock("div", {
        key: cpg.id
      }, [
        _createVNode(UmanaHeadline, {
          h: "h3",
          class: "line-clamp-1"
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(cpg.group_name), 1)
          ]),
          _: 2
        }, 1024),
        _createElementVNode("ul", _hoisted_3, [
          (
            currentMedication.value.find((cm) => cm.supplierId === cpg.id) ===
            undefined
          )
            ? (_openBlock(), _createElementBlock("p", _hoisted_4, " Keine Medikamente hinzugefügt "))
            : _createCommentVNode("", true),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(currentMedication.value.filter(
            (cm) => cm.supplierId === cpg.id
          ), (medication) => {
            return (_openBlock(), _createBlock(ListTile, {
              key: medication.pharmacode ?? medication.gtin,
              class: "first:pt-0"
            }, {
              main: _withCtx(() => [
                _createTextVNode(_toDisplayString(medication.dscrd) + " ", 1),
                _hoisted_5,
                _createElementVNode("span", _hoisted_6, [
                  (medication.file && documentWithPath(medication.file))
                    ? (_openBlock(), _createBlock(PatientFile, {
                        key: 0,
                        file: documentWithPath(medication.file),
                        small: true,
                        patient: managedPatient.value
                      }, null, 8, ["file", "patient"]))
                    : _createCommentVNode("", true)
                ]),
                (medication.gtin !== 0)
                  ? (_openBlock(), _createBlock(MedicationInformation, {
                      key: 0,
                      medication: medication,
                      "as-list": true,
                      "is-loading": isLoadingMedicationInformation.value,
                      error: errorLoadingMedicationInformation.value
                    }, null, 8, ["medication", "is-loading", "error"]))
                  : _createCommentVNode("", true)
              ]),
              trailing: _withCtx(() => [
                _createElementVNode("div", _hoisted_7, [
                  _createVNode(CoreIcon, {
                    onClick: ($event: any) => (removeMedication(medication.createdAt)),
                    icon: "trash"
                  }, null, 8, ["onClick"]),
                  _createVNode(CoreIcon, {
                    onClick: ($event: any) => (editMedication(medication)),
                    class: "ml-xs",
                    icon: "pen"
                  }, null, 8, ["onClick"])
                ])
              ]),
              _: 2
            }, 1024))
          }), 128))
        ])
      ]))
    }), 128)),
    _createVNode(DialogCard, {
      "is-visible": isAddingMedication.value,
      onClose: _cache[4] || (_cache[4] = ($event: any) => (isAddingMedication.value = false)),
      title: "Medikament Hinzufügen",
      "show-footer": false
    }, {
      default: _withCtx(() => [
        _createVNode(_component_FormKit, {
          type: "form",
          modelValue: medicationModel.value,
          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((medicationModel).value = $event)),
          onSubmit: saveMedication,
          class: "mt-5",
          "submit-label": "Medikament speichern"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_FormKit, {
              modelValue: selectedSupplier.value,
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((selectedSupplier).value = $event)),
              type: "select",
              name: "supplier",
              id: "supplier",
              label: "Zulieferer",
              options: 
            connectedShopCareProviderGroups.value?.length !== 0
              ? connectedShopCareProviderGroups.value?.map((cpg) => ({
                  label: cpg.group_name,
                  value: cpg.id,
                }))
              : [{ label: 'Kein Zulieferer zur Auswahl', value: -1 }]
          
            }, null, 8, ["modelValue", "options"]),
            _createVNode(_component_FormKit, {
              type: "medicationselect",
              name: "medication",
              id: "medication",
              label: "Medikament",
              validation: "required",
              shopCareProviderGroupId: selectedSupplier.value
            }, null, 8, ["shopCareProviderGroupId"]),
            _createVNode(_component_FormKit, {
              type: "multiselect",
              name: "file",
              "select-options": fileSelectOptions.value,
              "track-by": "path",
              optionLabel: "name",
              placeholder: "kein Zugehöriges Dokument",
              reset: "Kein zugehöriges Dokument",
              label: "Zugehöriges Dokument"
            }, null, 8, ["select-options"])
          ]),
          _: 1
        }, 8, ["modelValue"])
      ]),
      _: 1
    }, 8, ["is-visible"]),
    _createVNode(MedicationHistory, {
      "of-user": history.value,
      files: files.value,
      onClose: _cache[5] || (_cache[5] = ($event: any) => (history.value = null))
    }, null, 8, ["of-user", "files"])
  ]))
}
}

})